/**
 * PurchasesDashboard
 * 
 * Painel principal do módulo de compras.
 * Visão geral de todas as requisições e KPIs.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Truck, Search,
  Filter, MoreVertical, FileText,
  Microscope, Calculator, BrainCircuit,
  HandCoins, PackageCheck, FilePlus, CalendarClock,
  Download, Send, Eye, Trash2, X, RotateCcw, Database, MessageSquare, Loader2
} from 'lucide-react';
import { useRequisitions } from '../../../hooks/usePurchases';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal';
import Modal from '../../../components/ERP/Modal/Modal';
import { purchaseService } from '../../../services/purchasesApi';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './PurchasesDashboard.css';
import './FollowUpPage.css';

const PurchasesDashboard = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [activeMenu, setActiveMenu] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const menuRef = useRef(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  // Estados para geração de PDF da cotação
  const pdfQuotationRef = useRef(null);
  const pdfQuotationSummaryRef = useRef(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfQuotationData, setPdfQuotationData] = useState(null);
  const [engineLogs, setEngineLogs] = useState('Iniciando Motor...');

  // Estados para modal de detalhes da requisição
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [requisitionDetails, setRequisitionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Buscar requisições (otimizado: reduzido de 100 para 20 registros iniciais)
  const filters = useMemo(() => ({ 
    limit: itemsPerPage,
    page: currentPage
  }), [currentPage]);
  const { requisitions, loading, error, refetch, pagination } = useRequisitions(filters);
  
  // Workflow steps - usando state para permitir atualizações
  const [workflowSteps, setWorkflowSteps] = useState([
    { label: 'Requisição', count: 0, icon: FilePlus, colorClass: 'step-slate' },
    { label: 'Revisão Técnica', count: 0, icon: Microscope, colorClass: 'step-amber' },
    { label: 'Cotação', count: 0, icon: Calculator, colorClass: 'step-blue' },
    { label: 'Match Gemini', count: 0, icon: BrainCircuit, colorClass: 'step-indigo' },
    { label: 'Análise Fin.', count: 0, icon: HandCoins, colorClass: 'step-purple' },
    { label: 'Aprovado Fin.', count: 0, icon: PackageCheck, colorClass: 'step-emerald' },
  ]);

  // Calcular contagens por status - atualizar state em vez de mutar
  useEffect(() => {
    if (requisitions && Array.isArray(requisitions)) {
      setWorkflowSteps([
        { label: 'Requisição', count: requisitions.filter(r => r.status === 'draft').length, icon: FilePlus, colorClass: 'step-slate' },
        { label: 'Revisão Técnica', count: requisitions.filter(r => r.status === 'technical_review').length, icon: Microscope, colorClass: 'step-amber' },
        { label: 'Cotação', count: requisitions.filter(r => r.status === 'quoting').length, icon: Calculator, colorClass: 'step-blue' },
        { label: 'Match Gemini', count: requisitions.filter(r => r.status === 'fiscal_validation').length, icon: BrainCircuit, colorClass: 'step-indigo' },
        { label: 'Análise Fin.', count: requisitions.filter(r => r.status === 'finance_approval').length, icon: HandCoins, colorClass: 'step-purple' },
        { label: 'Aprovado Fin.', count: requisitions.filter(r => r.status === 'approved' || r.status === 'received').length, icon: PackageCheck, colorClass: 'step-emerald' },
      ]);
    }
  }, [requisitions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      draft: { label: 'Rascunho', className: 'status-draft', icon: FileText },
      technical_review: { label: 'Revisão Técnica', className: 'status-technical-review', icon: Microscope },
      quoting: { label: 'Em Cotação', className: 'status-quoting', icon: Calculator },
      fiscal_validation: { label: 'Validando IA', className: 'status-fiscal-validation', icon: BrainCircuit },
      finance_approval: { label: 'Análise Fin.', className: 'status-finance-approval', icon: HandCoins },
      approved: { label: 'Aprovado', className: 'status-approved', icon: PackageCheck },
      in_transit: { label: 'Em Trânsito', className: 'status-in-transit', icon: Truck },
      received: { label: 'Recebido', className: 'status-received', icon: PackageCheck },
      cancelled: { label: 'Cancelado', className: 'status-cancelled', icon: X }
    };
    return configs[status] || { label: status, className: 'status-default', icon: FileText };
  };

  const filteredRequisitions = useMemo(() => {
    if (!requisitions) return [];
    
    return requisitions.filter(req => {
      const matchesSearch =
        req.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.id?.toString().includes(searchTerm) ||
        req.requester_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = filter === 'all' || (filter === 'pending' && req.status !== 'completed' && req.status !== 'received');
      
      return matchesSearch && matchesFilter;
    });
  }, [requisitions, searchTerm, filter]);

  const handleQuoteReceived = async (req) => {
    try {
      const response = await purchaseService.updateStatus(req.id, 'fiscal_validation');
      if (response.success) {
        showToast('Cotação recebida! Status atualizado para análise com Gemini.', 'success');
        refetch();
      } else {
        showToast(response.message || 'Erro ao atualizar status', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showToast(error.response?.data?.message || 'Erro ao atualizar status', 'error');
    }
  };

  const handleDownloadQuotation = async (req) => {
    try {
      setActiveMenu(null);
      
      // Buscar dados completos da requisição
      setGeneratingPDF(true);
      setEngineLogs('Carregando dados da cotação...');
      
      const response = await purchaseService.getById(req.id);
      if (!response.success || !response.data) {
        showToast('Erro ao carregar dados da cotação', 'error');
        setGeneratingPDF(false);
        return;
      }

      const requisitionData = response.data;
      
      // Buscar itens da requisição se não vierem no response
      let items = requisitionData.items || [];
      if (!items || items.length === 0) {
        // Se não tiver itens, tentar buscar separadamente
        // Por enquanto, vamos usar os dados que temos
        items = [];
      }

      // Preparar dados para o PDF
      const quotationData = {
        ...requisitionData,
        items: items,
        quotation_date: new Date().toISOString(),
        quotation_number: `COT-${new Date().getFullYear()}-${String(requisitionData.id).slice(-4)}`
      };

      setPdfQuotationData(quotationData);
      setEngineLogs('Preparando documento...');
      
      // Aguardar um pouco para garantir que os componentes foram renderizados
      await new Promise(r => setTimeout(r, 500));
      
      if (!pdfQuotationRef.current || !pdfQuotationSummaryRef.current) {
        showToast('Erro ao gerar PDF. Tente novamente.', 'error');
        setGeneratingPDF(false);
        return;
      }

      setEngineLogs('Renderizando documento...');
      
      // Renderizar duas partes separadas: itens e resumo
      const [itemsCanvas, summaryCanvas] = await Promise.all([
        html2canvas(pdfQuotationRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#FFFFFF",
          logging: false,
          windowWidth: 1200,
          allowTaint: false,
          removeContainer: false,
        }),
        html2canvas(pdfQuotationSummaryRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#FFFFFF",
          logging: false,
          windowWidth: 1200,
          allowTaint: false,
          removeContainer: false,
        })
      ]);
      
      setEngineLogs('Gerando PDF...');
      await new Promise(r => setTimeout(r, 400));
      
      const itemsImgData = itemsCanvas.toDataURL('image/jpeg', 0.98);
      const summaryImgData = summaryCanvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Margens do PDF
      const margin = 10;
      const spacing = 5;
      const contentWidth = pdfWidth - (margin * 2);
      
      // Calcular dimensões das imagens proporcionalmente
      const itemsImgHeight = (itemsCanvas.height * contentWidth) / itemsCanvas.width;
      const summaryImgHeight = (summaryCanvas.height * contentWidth) / summaryCanvas.width;
      
      // Altura disponível por página
      const availableHeightPerPage = pdfHeight - (margin * 2);
      
      setEngineLogs('Finalizando...');
      
      // === PARTE 1: Adicionar seção de itens ===
      let currentY = margin;
      let itemsHeightRemaining = itemsImgHeight;
      let itemsSourceY = 0;
      
      // Primeira página: adicionar o máximo possível da seção de itens
      pdf.addImage(
        itemsImgData, 
        'JPEG', 
        margin, 
        currentY, 
        contentWidth, 
        itemsImgHeight, 
        undefined, 
        'FAST'
      );
      
      itemsHeightRemaining -= availableHeightPerPage;
      itemsSourceY = availableHeightPerPage;
      currentY = margin;
      
      // Adicionar páginas adicionais se os itens não couberem em uma página
      while (itemsHeightRemaining > 0) {
        pdf.addPage();
        currentY = margin;
        
        const yOffset = -(itemsSourceY - margin);
        
        pdf.addImage(
          itemsImgData, 
          'JPEG', 
          margin, 
          currentY + yOffset, 
          contentWidth, 
          itemsImgHeight, 
          undefined, 
          'FAST'
        );
        
        itemsHeightRemaining -= availableHeightPerPage;
        itemsSourceY += availableHeightPerPage;
      }
      
      // Calcular quanto de altura foi usado na última página de itens
      const lastPageItemsHeight = itemsImgHeight % availableHeightPerPage || 
                                  (itemsImgHeight <= availableHeightPerPage ? itemsImgHeight : availableHeightPerPage);
      
      // === PARTE 2: Adicionar seção de resumo ===
      const spaceLeftInCurrentPage = availableHeightPerPage - lastPageItemsHeight;
      const summaryWithSpacing = summaryImgHeight + spacing;
      const needsNewPage = summaryWithSpacing > spaceLeftInCurrentPage;
      
      if (needsNewPage) {
        pdf.addPage();
        currentY = margin;
        
        if (summaryImgHeight <= availableHeightPerPage) {
          pdf.addImage(
            summaryImgData, 
            'JPEG', 
            margin, 
            currentY, 
            contentWidth, 
            summaryImgHeight, 
            undefined, 
            'FAST'
          );
        } else {
          let summaryHeightRemaining = summaryImgHeight;
          let summarySourceY = 0;
          
          while (summaryHeightRemaining > 0) {
            if (summarySourceY > 0) {
              pdf.addPage();
              currentY = margin;
            }
            
            const yOffset = -(summarySourceY - margin);
            pdf.addImage(
              summaryImgData, 
              'JPEG', 
              margin, 
              currentY + yOffset, 
              contentWidth, 
              summaryImgHeight, 
              undefined, 
              'FAST'
            );
            
            summaryHeightRemaining -= availableHeightPerPage;
            summarySourceY += availableHeightPerPage;
          }
        }
      } else {
        currentY = margin + lastPageItemsHeight + spacing;
        pdf.addImage(
          summaryImgData, 
          'JPEG', 
          margin, 
          currentY, 
          contentWidth, 
          summaryImgHeight, 
          undefined, 
          'FAST'
        );
      }
      
      setEngineLogs('Concluído!');
      await new Promise(r => setTimeout(r, 400));
      
      const fileName = `Cotacao_Requisicao_${req.id}_${new Date().getFullYear()}_${Date.now()}.pdf`;
      pdf.save(fileName);
      
      showToast('PDF gerado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      showToast('Erro ao gerar PDF. Tente novamente.', 'error');
    } finally {
      setGeneratingPDF(false);
      setPdfQuotationData(null);
    }
  };

  const handleSendQuotation = async (req) => {
    setConfirmModal({
      isOpen: true,
      title: 'Enviar Cotação',
      message: `Deseja enviar a cotação da requisição ${req.id} por email?`,
      type: 'info',
      onConfirm: async () => {
        try {
          const response = await purchaseService.sendQuotation(req.id);
          if (response.success) {
            showToast('Cotação enviada com sucesso!', 'success');
          } else {
            showToast(response.message || 'Erro ao enviar cotação', 'error');
          }
        } catch (error) {
          console.error('Erro ao enviar cotação:', error);
          showToast(error.response?.data?.message || 'Erro ao enviar cotação', 'error');
        }
      }
    });
  };

  // Enviar cotação via WhatsApp
  const handleSendQuotationWhatsApp = async (req) => {
    try {
      setActiveMenu(null);
      
      // Buscar dados completos da requisição
      const response = await purchaseService.getById(req.id);
      if (!response.success || !response.data) {
        showToast('Erro ao carregar dados da cotação', 'error');
        return;
      }

      const requisitionData = response.data;
      const quotationNumber = `COT-${new Date().getFullYear()}-${String(requisitionData.id).slice(-4)}`;

      // Formatar mensagem para WhatsApp
      const formatWhatsAppMessage = (data) => {
        let message = `*COTAÇÃO DE COMPRA*\n\n`;
        message += `*ID:* ${data.id || 'N/A'}\n`;
        message += `*Título:* ${data.title || 'Sem título'}\n`;
        message += `*Número da Cotação:* ${quotationNumber}\n\n`;
        
        message += `*SOLICITANTE*\n`;
        message += `Nome: ${data.requester_name || 'N/A'}\n`;
        if (data.requester_email) {
          message += `Email: ${data.requester_email}\n`;
        }
        message += `\n`;
        
        message += `*VALORES*\n`;
        const totalValue = parseFloat(data.total_value || 0);
        message += `Valor Total Estimado: R$ ${totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
        message += `\n`;
        
        if (data.items && data.items.length > 0) {
          message += `*ITENS DA COTAÇÃO*\n`;
          data.items.slice(0, 10).forEach((item, idx) => {
            message += `${idx + 1}. ${item.product_name || item.description || 'Produto não informado'}\n`;
            message += `   Qtd: ${item.quantity || item.requested_qty || 0}\n`;
            if (item.unit_price) {
              message += `   Valor Unit: R$ ${parseFloat(item.unit_price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
            }
            if (item.total_price || item.item_total) {
              message += `   Total: R$ ${parseFloat(item.total_price || item.item_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
            }
            message += `\n`;
          });
          if (data.items.length > 10) {
            message += `... e mais ${data.items.length - 10} item(ns)\n\n`;
          }
        }
        
        message += `_Esta é uma solicitação de cotação de preços. Valores sujeitos à confirmação._`;
        
        return encodeURIComponent(message);
      };
      
      // Obter número do WhatsApp (se houver fornecedor)
      let phoneNumber = '';
      if (requisitionData.supplier) {
        const phone = requisitionData.supplier.cellphone || 
                     requisitionData.supplier.phone || 
                     requisitionData.supplier.contact_phone || 
                     '';
        const cleanPhone = phone.toString().replace(/\D/g, '');
        if (cleanPhone && cleanPhone.length >= 10) {
          phoneNumber = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
        }
      }
      
      // Se não tiver número, usar prompt para o usuário informar
      if (!phoneNumber) {
        const userPhone = prompt('Informe o número do WhatsApp (com DDD, ex: 11987654321):');
        if (userPhone) {
          const cleanPhone = userPhone.replace(/\D/g, '');
          if (cleanPhone && cleanPhone.length >= 10) {
            phoneNumber = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
          }
        }
      }
      
      if (!phoneNumber) {
        showToast('Número de WhatsApp não informado', 'warning');
        return;
      }
      
      // Criar link do WhatsApp com mensagem
      const message = formatWhatsAppMessage(requisitionData);
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
      
      // Abrir WhatsApp em nova aba
      window.open(whatsappUrl, '_blank');
      
      showToast('Abrindo WhatsApp...', 'success');
    } catch (error) {
      console.error('Erro ao enviar cotação via WhatsApp:', error);
      showToast('Erro ao preparar envio via WhatsApp. Tente novamente.', 'error');
    }
  };

  const handleAction = (req) => {
    if (req.status === 'fiscal_validation') {
      navigate(`/erp/purchases/mirrors/verify/${req.id}`);
    } else if (req.status === 'technical_review') {
      navigate(`/erp/purchases/review/${req.id}`);
    } else if (req.status === 'finance_approval') {
      navigate(`/erp/purchases/finance/approve/${req.id}`);
    } else if (req.status === 'quoting') {
      // Quando status é "quoting", não fazer nada ao clicar na linha
      // A mudança de status só ocorre quando o usuário clicar no botão "Cotação Recebida"
      return;
    } else if (req.status === 'approved' || req.status === 'received') {
      navigate(`/erp/purchases/follow-up`);
    } else if (req.status === 'draft') {
      // Navegar para editar rascunho com ID
      navigate(`/erp/purchases/new-request/${req.id}`);
    } else {
      navigate('/erp/purchases/new-request');
    }
  };

  const handleCancelRequisition = (req) => {
    setConfirmModal({
      isOpen: true,
      title: 'Cancelar Requisição',
      message: `Deseja realmente cancelar a requisição ${req.id}? Esta ação não pode ser desfeita.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await purchaseService.updateStatus(req.id, 'cancelled');
          if (response.success) {
            showToast('Requisição cancelada com sucesso!', 'success');
            refetch();
          } else {
            showToast(response.message || 'Erro ao cancelar requisição', 'error');
          }
        } catch (error) {
          console.error('Erro ao cancelar requisição:', error);
          showToast(error.response?.data?.message || 'Erro ao cancelar requisição', 'error');
        }
      }
    });
  };

  const handleSendToDraft = (req) => {
    setConfirmModal({
      isOpen: true,
      title: 'Enviar para Rascunho',
      message: `Deseja retornar a requisição ${req.id} para rascunho? Ela poderá ser editada novamente.`,
      type: 'warning',
      onConfirm: async () => {
        try {
          const response = await purchaseService.updateStatus(req.id, 'draft');
          
          if (response && response.success) {
            showToast('Requisição retornada para rascunho com sucesso!', 'success');
            // Aguardar um pouco antes de refetch para garantir que o backend processou
            await new Promise(resolve => setTimeout(resolve, 300));
            await refetch();
          } else {
            const errorMsg = response?.message || 'Erro ao retornar para rascunho';
            showToast(errorMsg, 'error');
          }
        } catch (error) {
          console.error('Erro ao retornar para rascunho:', error);
          showToast(error.response?.data?.message || error.message || 'Erro ao retornar para rascunho', 'error');
        }
      }
    });
  };

  const handleSendToTechnicalReview = (req) => {
    setConfirmModal({
      isOpen: true,
      title: 'Retornar para Revisão Técnica',
      message: `Deseja retornar a requisição ${req.id} para revisão técnica? Ela será reanalisada pela equipe técnica.`,
      type: 'warning',
      onConfirm: async () => {
        try {
          const response = await purchaseService.updateStatus(req.id, 'technical_review');
          
          if (response && response.success) {
            showToast('Requisição retornada para revisão técnica com sucesso!', 'success');
            // Aguardar um pouco antes de refetch para garantir que o backend processou
            await new Promise(resolve => setTimeout(resolve, 300));
            await refetch();
          } else {
            const errorMsg = response?.message || 'Erro ao retornar para revisão técnica';
            showToast(errorMsg, 'error');
          }
        } catch (error) {
          console.error('Erro ao retornar para revisão técnica:', error);
          showToast(error.response?.data?.message || error.message || 'Erro ao retornar para revisão técnica', 'error');
        }
      }
    });
  };

  const handleViewDetails = async (req) => {
    try {
      setActiveMenu(null);
      setLoadingDetails(true);
      setDetailsModalOpen(true);
      
      const response = await purchaseService.getById(req.id);
      if (response.success) {
        setRequisitionDetails(response.data);
      } else {
        showToast('Erro ao carregar detalhes da requisição', 'error');
        setDetailsModalOpen(false);
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes da requisição:', error);
      showToast('Erro ao carregar detalhes da requisição. Tente novamente.', 'error');
      setDetailsModalOpen(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Skeleton loader apenas no primeiro carregamento inicial (quando não há dados ainda e não há erro)
  // Se já temos dados, mostra a página mesmo durante loading (ex: mudança de página)
  const isInitialLoad = loading && (!requisitions || requisitions.length === 0) && !error;

  // Renderizar página principal (mesmo durante loading se já temos dados)
  return (
    <div className="purchases-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Gestão de <span className="highlight">Requisições</span></h1>
            <p className="subtitle">Esteira Inteligente de Suprimentos B2B</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn-secondary"
              onClick={() => navigate('/erp/purchases/minimum-stock')}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Database size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Mensagem de erro inline */}
      {error && (
        <div className="error-banner" style={{ 
          padding: '12px 16px', 
          marginBottom: '16px', 
          backgroundColor: '#fee', 
          border: '1px solid #fcc', 
          borderRadius: '8px',
          color: '#c33'
        }}>
          <AlertTriangle size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {error}
          <button 
            onClick={() => refetch()}
            style={{ 
              marginLeft: '12px', 
              padding: '4px 12px', 
              background: '#c33', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Process Flow Map */}
      <div className="workflow-card">
        <div className="workflow-header">
          <h3>
            <CalendarClock size={16} />
            Cronograma de Compras
          </h3>
          <span className="workflow-average-time">Tempo médio de ciclo: 4.2 dias</span>
        </div>

        <div className="workflow-steps">
          {workflowSteps.map((step, idx) => {
            const Icon = step.icon || FileText; // Fallback para garantir que sempre tenha ícone
            return (
              <div
                key={step.label}
                className={`workflow-step ${step.colorClass}`}
                onClick={() => {
                  if (step.label === 'Requisição') navigate('/erp/purchases/draft-list');
                  if (step.label === 'Revisão Técnica') navigate('/erp/purchases/review-list');
                  if (step.label === 'Match Gemini') navigate('/erp/purchases/mirrors');
                  if (step.label === 'Análise Fin.') navigate('/erp/purchases/finance');
                }}
              >
                <div className="step-icon-wrapper">
                  {Icon && <Icon size={24} />}
                  {step.count > 0 && (
                    <div className="step-badge">{step.count}</div>
                  )}
                </div>
                <p className="step-label">{step.label}</p>
                {idx < workflowSteps.length - 1 && (
                  <div className="step-connector" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main List Container */}
      <div className="requisitions-card">
        <div className="requisitions-toolbar">
          <div className="search-wrapper">
            <Search className={`search-icon ${searchTerm ? 'active' : ''}`} size={16} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar requisição, solicitante ou ID..."
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              Todos
            </button>
            <button
              className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pendentes
            </button>
            <div className="filter-divider" />
            <button className="filter-icon-btn">
              <Filter size={18} />
            </button>
          </div>
        </div>

        <div className="requisitions-table-wrapper">
          <table className="requisitions-table">
            <thead>
              <tr>
                <th>Identificação / Solicitante</th>
                <th className="text-center">Itens</th>
                <th>Previsão Total</th>
                <th className="text-center">Status Atual</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoad ? (
                // Skeleton durante carregamento inicial
                [1, 2, 3, 4, 5].map((i) => (
                  <tr key={i} className="requisition-row skeleton-row">
                    <td>
                      <div className="skeleton-text" style={{ width: '200px', height: '16px', marginBottom: '8px' }}></div>
                      <div className="skeleton-text" style={{ width: '150px', height: '12px' }}></div>
                    </td>
                    <td className="text-center">
                      <div className="skeleton-text" style={{ width: '60px', height: '20px', margin: '0 auto' }}></div>
                    </td>
                    <td>
                      <div className="skeleton-text" style={{ width: '100px', height: '16px', marginBottom: '4px' }}></div>
                      <div className="skeleton-text" style={{ width: '80px', height: '12px' }}></div>
                    </td>
                    <td className="text-center">
                      <div className="skeleton-text" style={{ width: '120px', height: '24px', margin: '0 auto' }}></div>
                    </td>
                    <td></td>
                  </tr>
                ))
              ) : filteredRequisitions.length > 0 ? (
                filteredRequisitions.map((req) => {
                  const config = getStatusConfig(req.status);
                  const Icon = config.icon;
                  const isQuote = req.status === 'quoting';
                  
                  return (
                    <tr
                      key={req.id}
                      className={`requisition-row ${isQuote ? 'no-click-action' : ''}`}
                      onClick={() => {
                        // Quando status é "quoting", não fazer nada ao clicar na linha
                        if (req.status !== 'quoting') {
                          handleAction(req);
                        }
                      }}
                    >
                      <td>
                        <div className="requisition-info">
                          <span className="requisition-title">{req.title}</span>
                          <div className="requisition-meta">
                            <span className="requisition-id">#{req.id}</span>
                            <span className="requisition-requester">• Por {req.requester_name || 'N/A'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="items-badge">{req.items_count || 0} unid.</span>
                      </td>
                      <td>
                        <p className="requisition-value">{formatCurrency(req.total_value || 0)}</p>
                        <p className="requisition-date">{formatDate(req.created_at)}</p>
                      </td>
                      <td className="text-center">
                        <span className={`status-badge ${config.className}`}>
                          <Icon size={12} />
                          {config.label}
                        </span>
                      </td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="row-actions">
                          {req.status !== 'completed' && req.status !== 'received' && (
                            <button
                              className="btn-process"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (req.status === 'quoting') {
                                  // Quando status é "quoting", chamar diretamente handleQuoteReceived
                                  handleQuoteReceived(req);
                                } else {
                                  handleAction(req);
                                }
                              }}
                            >
                              {req.status === 'quoting' ? 'Cotação Recebida' : 'Processar'}
                            </button>
                          )}
                          
                          <div className="menu-wrapper" ref={menuRef}>
                            <button
                              className={`menu-trigger ${activeMenu === req.id ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenu(activeMenu === req.id ? null : req.id);
                              }}
                            >
                              <MoreVertical size={20} />
                            </button>

                            {activeMenu === req.id && (
                              <div 
                                className="dropdown-menu"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <button 
                                  className="dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenu(null);
                                    handleViewDetails(req);
                                  }}
                                >
                                  <Eye size={14} />
                                  Detalhes da Requisição
                                </button>
                                
                                {isQuote && (
                                  <>
                                    <button 
                                      className="dropdown-item success"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenu(null);
                                        handleDownloadQuotation(req);
                                      }}
                                    >
                                      <Download size={14} />
                                      Baixar Cotação
                                    </button>
                                    <button 
                                      className="dropdown-item info"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenu(null);
                                        handleSendQuotation(req);
                                      }}
                                    >
                                      <Send size={14} />
                                      Enviar Cotação
                                    </button>
                                    <button 
                                      className="dropdown-item follow-up-menu-item-emerald"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenu(null);
                                        handleSendQuotationWhatsApp(req);
                                      }}
                                    >
                                      <MessageSquare size={14} />
                                      Enviar PDF via WhatsApp
                                    </button>
                                  </>
                                )}

                                {req.status === 'fiscal_validation' && (
                                  <>
                                    <div className="dropdown-divider" />
                                    <button 
                                      className="dropdown-item warning"
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setActiveMenu(null);
                                        handleSendToTechnicalReview(req);
                                      }}
                                    >
                                      <RotateCcw size={14} />
                                      Retornar para Revisão Técnica
                                    </button>
                                  </>
                                )}

                                {req.status === 'technical_review' && (
                                  <>
                                    <div className="dropdown-divider" />
                                    <button 
                                      className="dropdown-item warning"
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setActiveMenu(null);
                                        handleSendToDraft(req);
                                      }}
                                    >
                                      <RotateCcw size={14} />
                                      Enviar para Rascunho
                                    </button>
                                  </>
                                )}

                                {req.status === 'finance_approval' && (
                                  <>
                                    <div className="dropdown-divider" />
                                    <button 
                                      className="dropdown-item warning"
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setActiveMenu(null);
                                        handleSendToDraft(req);
                                      }}
                                    >
                                      <RotateCcw size={14} />
                                      Enviar para Rascunho
                                    </button>
                                  </>
                                )}

                                {req.status !== 'received' && req.status !== 'cancelled' && (
                                  <>
                                    <div className="dropdown-divider" />
                                    <button 
                                      className="dropdown-item danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenu(null);
                                        handleCancelRequisition(req);
                                      }}
                                    >
                                      <Trash2 size={14} />
                                      Cancelar Requisição
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="empty-state">
                    <div className="empty-content">
                      <Search size={48} />
                      <p>Nenhuma requisição encontrada para os filtros aplicados</p>
                      <button
                        className="btn-link"
                        onClick={() => { setFilter('all'); setSearchTerm(''); }}
                      >
                        Limpar todos os filtros
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination && pagination.totalPages > 1 && (
          <div className="pagination-controls">
            <div className="pagination-info">
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, pagination.total)} de {pagination.total} requisições
            </div>
            <div className="pagination-buttons">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || loading}
              >
                Anterior
              </button>
              <span className="pagination-page">
                Página {currentPage} de {pagination.totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage === pagination.totalPages || loading}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' })}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
        }}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      {/* Modal de Detalhes da Requisição */}
      <Modal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setRequisitionDetails(null);
        }}
        title="Detalhes da Requisição"
        icon={<FileText size={24} />}
        size="large"
        footer={
          <button
            className="btn-primary"
            onClick={() => {
              setDetailsModalOpen(false);
              setRequisitionDetails(null);
            }}
          >
            Fechar
          </button>
        }
      >
        {loadingDetails ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <Loader2 size={32} className="spinner" style={{ margin: '0 auto', display: 'block' }} />
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Carregando detalhes da requisição...</p>
          </div>
        ) : requisitionDetails ? (
          <div style={{ padding: '1rem 0' }}>
            {/* Informações Gerais */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                Informações Gerais
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '1rem' 
              }}>
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    ID da Requisição
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                    #{requisitionDetails.id || 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Título
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                    {requisitionDetails.title || 'Sem título'}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Status
                  </span>
                  <span className={`status-badge ${getStatusConfig(requisitionDetails.status).className}`}>
                    {getStatusConfig(requisitionDetails.status).label}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Prioridade
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                    {requisitionDetails.priority === 'urgent' ? 'Urgente' :
                     requisitionDetails.priority === 'critical' ? 'Crítica' :
                     requisitionDetails.priority === 'low' ? 'Baixa' : 'Normal'}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Solicitante
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                    {requisitionDetails.requester_name || requisitionDetails.requester_email || 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Data de Criação
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                    {requisitionDetails.created_at 
                      ? new Date(requisitionDetails.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Fornecedor */}
            {requisitionDetails.supplier_id && requisitionDetails.supplier && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                  Fornecedor
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                  gap: '1rem' 
                }}>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      Nome
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                      {requisitionDetails.supplier.name || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      CNPJ/CPF
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                      {requisitionDetails.supplier.cpf_cnpj || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      E-mail
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                      {requisitionDetails.supplier.email || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      Telefone
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                      {requisitionDetails.supplier.phone || requisitionDetails.supplier.cellphone || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Valores */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                Valores
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '1rem' 
              }}>
                <div>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                    Valor Total
                  </span>
                  <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#059669' }}>
                    {formatCurrency(requisitionDetails.total_value || requisitionDetails.invoice_total || 0)}
                  </span>
                </div>
                {requisitionDetails.invoice_number && (
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                      Número da Nota Fiscal
                    </span>
                    <span style={{ fontSize: '1rem', fontWeight: 500, color: '#1f2937' }}>
                      {requisitionDetails.invoice_number}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Itens da Requisição */}
            {requisitionDetails.items && requisitionDetails.items.length > 0 && (
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                  Itens da Requisição ({requisitionDetails.items.length})
                </h4>
                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                          Descrição
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                          Qtd
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                          Unitário
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {requisitionDetails.items.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#1f2937' }}>
                            {item.product_name || item.description || 'Produto não informado'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', color: '#1f2937' }}>
                            {item.quantity || item.requested_qty || 0}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', color: '#1f2937' }}>
                            {item.unit_price 
                              ? formatCurrency(item.unit_price)
                              : '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}>
                            {item.total_price || item.item_total
                              ? formatCurrency(item.total_price || item.item_total)
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Observações */}
            {requisitionDetails.observation && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1f2937' }}>
                  Observações
                </h4>
                <div style={{ 
                  padding: '1rem', 
                  background: '#f9fafb', 
                  borderRadius: '8px', 
                  border: '1px solid #e5e7eb',
                  fontSize: '0.875rem',
                  color: '#374151',
                  whiteSpace: 'pre-wrap'
                }}>
                  {requisitionDetails.observation}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            Nenhum detalhe disponível
          </div>
        )}
      </Modal>

      {/* Overlay de Geração de PDF */}
      {generatingPDF && (
        <div className="follow-up-pdf-overlay">
          <div className="follow-up-pdf-overlay-content">
            <div className="follow-up-pdf-icon-wrapper">
              <Calculator className="spinner" size={32} />
              <div className="follow-up-pdf-scan" />
            </div>
            <h4 className="follow-up-pdf-title">Gerando PDF</h4>
            <p className="follow-up-pdf-subtitle">Exportando Cotação</p>
            <div className="follow-up-pdf-log">
              <p className="follow-up-pdf-log-text">{engineLogs}</p>
            </div>
            <div className="follow-up-pdf-progress-bar">
              <div className="follow-up-pdf-progress-fill" />
            </div>
          </div>
        </div>
      )}

      {/* Área oculta para geração de PDF da Cotação */}
      {pdfQuotationData && (
        <>
          {/* Container principal com cabeçalho e itens */}
          <div ref={pdfQuotationRef} className="order-pdf-container" style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm', backgroundColor: '#FFFFFF' }}>
            {/* Cabeçalho */}
            <div className="order-pdf-header">
              <div className="order-pdf-header-decoration" />
              <div className="order-pdf-header-content">
                <div className="order-pdf-logo">
                  <div className="order-pdf-logo-icon">PM</div>
                  <span className="order-pdf-logo-text">med</span>
                </div>
                <h1 className="order-pdf-title">Cotação de Compra</h1>
                <p className="order-pdf-doc-number">
                  Doc: {pdfQuotationData.quotation_number || `COT-${new Date().getFullYear()}-${String(pdfQuotationData.id || Date.now()).slice(-4)}`}
                </p>
              </div>
              <div className="order-pdf-header-info">
                <div className="order-pdf-info-group">
                  <p className="order-pdf-info-label">Data da Cotação</p>
                  <p className="order-pdf-info-value">
                    {pdfQuotationData.quotation_date 
                      ? new Date(pdfQuotationData.quotation_date).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
                      : new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div className="order-pdf-info-group">
                  <p className="order-pdf-info-label">Status</p>
                  <p className="order-pdf-info-value primary">
                    Em Cotação
                  </p>
                </div>
              </div>
            </div>

            {/* Corpo do Documento */}
            <div className="order-pdf-body">
              {/* Informações Gerais */}
              <div className="order-pdf-parties">
                <div className="order-pdf-party">
                  <h4 className="order-pdf-party-title">Dados da Requisição</h4>
                  <div>
                    <p className="order-pdf-party-name">{pdfQuotationData.title || 'Sem título'}</p>
                    <p className="order-pdf-party-detail">ID: #{pdfQuotationData.id || 'N/A'}</p>
                    <p className="order-pdf-party-cnpj">
                      Solicitante: {pdfQuotationData.requester_name || pdfQuotationData.requester_email || 'N/A'}
                    </p>
                    <p className="order-pdf-party-cnpj">
                      Prioridade: {pdfQuotationData.priority === 'urgent' ? 'Urgente' :
                                  pdfQuotationData.priority === 'critical' ? 'Crítica' :
                                  pdfQuotationData.priority === 'low' ? 'Baixa' : 'Normal'}
                    </p>
                    <p className="order-pdf-party-cnpj">
                      Criado em: {pdfQuotationData.created_at 
                        ? new Date(pdfQuotationData.created_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabela de Itens */}
              {pdfQuotationData.items && pdfQuotationData.items.length > 0 ? (
                <div className="order-pdf-section">
                  <table className="order-pdf-table">
                    <thead>
                      <tr>
                        <th>Descrição do Produto</th>
                        <th className="order-pdf-table-center">Qtd</th>
                        <th className="order-pdf-table-right">Unitário</th>
                        <th className="order-pdf-table-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pdfQuotationData.items.map((item, idx) => (
                        <tr key={idx} className="order-pdf-table-row">
                          <td>
                            <p className="order-pdf-product-name">{item.product_name || item.description || 'Produto não informado'}</p>
                            <p className="order-pdf-product-note">Item #{idx + 1}</p>
                          </td>
                          <td className="order-pdf-table-center">{item.quantity || item.requested_qty || 0}</td>
                          <td className="order-pdf-table-right">
                            {item.unit_price 
                              ? `R$ ${parseFloat(item.unit_price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '-'}
                          </td>
                          <td className="order-pdf-table-right">
                            {item.total_price || item.item_total
                              ? `R$ ${parseFloat(item.total_price || item.item_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="order-pdf-section">
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Nenhum item cadastrado nesta requisição.</p>
                </div>
              )}
            </div>
          </div>

          {/* Container separado para resumo */}
          <div ref={pdfQuotationSummaryRef} className="order-pdf-container order-pdf-last-page" style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm', backgroundColor: '#FFFFFF', padding: '10mm' }}>
            <div className="order-pdf-summary">
              <div className="order-pdf-notes">
                <h4 className="order-pdf-notes-title">
                  <FileText size={14} /> Informações da Cotação
                </h4>
                <ul className="order-pdf-notes-list">
                  <li>• Esta é uma solicitação de cotação de preços.</li>
                  <li>• Os valores apresentados são estimativas baseadas na requisição.</li>
                  <li>• Valores finais sujeitos à confirmação do fornecedor.</li>
                  <li>• Prazo de validade da cotação: 30 dias.</li>
                </ul>
              </div>
              <div className="order-pdf-totals">
                <div className="order-pdf-final-total">
                  <p className="order-pdf-final-total-label">Valor Total Estimado</p>
                  <p className="order-pdf-final-total-value">
                    R$ {parseFloat(pdfQuotationData.total_value || 0)
                      .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PurchasesDashboard;
